import React, { forwardRef, } from "react";
import styled from "styled-components";
import Icon from "../icons/Icon";
import { getValueFromPixelString } from "../../utils/TokenHelpers";
import { apolloFontFamilyBodySmall, apolloFontSizeBodySmall, apolloLineHeightBodySmall, apolloSpaceInset1X, apolloSpaceInset2X, apolloSpaceInset3X, apolloSpaceInset6X, apolloSpaceInsetHalfX, } from "@nexthink/apollo-tokens";
import apolloTheme from "../../theme/Theme";
import Button from "../button/Button";
import { getDropDownStyles } from "../dropdown/Dropdown.style";
import { StyledMenuItem } from "../menu/MenuItem.style";
import { StyledMenu } from "../menu/Menu.style";
import Text from "../text/Text";
import { focusVisibleAttribute } from "../../utils/focusVisible";
export const StyledIcon = styled(({ className }) => React.createElement(Icon, { className: className, name: "angleDown" })).withConfig({ displayName: "StyledIcon_APC-7281-productShell", componentId: "sc-1w5gi3l" })(({ theme, isDisabled }) => (Object.assign({ display: "block", fontSize: "1rem", pointerEvents: "none", position: "absolute", right: "1px", top: "50%", transform: "translateY(-50%)" }, (isDisabled && {
    color: theme.colors.apolloColorTextDisabled,
}))));
export const StyledErrorIconContainer = styled.span.withConfig({ displayName: "StyledErrorIconContainer_APC-7281-productShell", componentId: "sc-1i2i86p" })({
    marginRight: `calc(${apolloSpaceInsetHalfX} + ${apolloSpaceInset2X})`,
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    left: "auto",
    right: 0,
    width: "16px",
    height: "16px",
});
export const StyledAndMoreText = styled(Text).withConfig({ displayName: "StyledAndMoreText_APC-7281-productShell", componentId: "sc-rl3vq0" })(({ theme }) => ({
    display: "block",
    color: theme.colors.apolloColorTextSecondary,
}));
export const StyledDropdown = styled.div.withConfig({ displayName: "StyledDropdown_APC-7281-productShell", componentId: "sc-71h5wv" })(({ theme, forcedWidth, $width, dropdownSameWidth, dropdownMaxWidth }) => {
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, getDropDownStyles(theme, true)), { height: undefined, background: theme.colors.apolloColorBackgroundMenuItemDefault }), (!dropdownSameWidth && {
        width: "max-content",
    })), (forcedWidth && {
        width: `${forcedWidth}px`,
    })), (dropdownMaxWidth && {
        maxWidth: dropdownMaxWidth,
        minWidth: $width,
    })), (!dropdownSameWidth &&
        !dropdownMaxWidth && {
        minWidth: $width,
        [`${StyledMenuItem}`]: {
            "& > span, & > span > span": {
                overflow: "visible",
            },
        },
        [`${StyledMenu}`]: {
            overflowX: "hidden",
        },
    }));
});
const getButtonStateStyle = (size, error, removeBorders = false) => {
    const paddingVertical = size === "normal" ? apolloSpaceInsetHalfX : apolloSpaceInset1X;
    const paddingRight = error ? apolloSpaceInset6X : apolloSpaceInset3X;
    const paddingOffset = removeBorders ? 0 : 1;
    return {
        borderWidth: removeBorders ? 0 : 1,
        paddingTop: `calc(${paddingVertical} - ${paddingOffset}px)`,
        paddingBottom: `calc(${paddingVertical} - ${paddingOffset}px)`,
        paddingLeft: `calc(${apolloSpaceInset1X} - ${paddingOffset}px)`,
        paddingRight: `calc(${paddingRight} - ${paddingOffset}px)`,
        // override padding for dropdown button
        [StyledIcon]: {
            marginRight: removeBorders ? 1 : 0,
        },
    };
};
const getButtonHeight = (size) => `calc(${apolloLineHeightBodySmall} + 2 * ${size === "normal" ? apolloSpaceInsetHalfX : apolloSpaceInset1X})`;
// focusSelector to override the global style
const focusSelector = `[${focusVisibleAttribute}][data-focus-border]`;
export const getButtonStyles = (theme, isDisabled, isOpen, error, size) => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ appearance: "none", background: theme.colors.apolloColorBackgroundFormElementDefault, borderColor: theme.colors.apolloColorStrokeFormElementDefault, borderRadius: "4px", borderStyle: "solid", boxShadow: theme.colors.apolloBoxShadowFormElement, boxSizing: "border-box", color: theme.colors.apolloColorTextPrimary, cursor: "pointer", display: "inline-block", font: `${apolloFontSizeBodySmall} ${apolloFontFamilyBodySmall}`, height: getButtonHeight(size), lineHeight: apolloLineHeightBodySmall, outline: "none", overflow: "hidden", verticalAlign: "middle", position: "relative", textAlign: "left", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }, getButtonStateStyle(size, error)), { "&:hover": Object.assign({ background: theme.colors.apolloColorBackgroundFormElementHover, borderColor: theme.colors.apolloColorStrokeFormElementHover, boxShadow: theme.colors.apolloBoxShadowFormElement }, getButtonStateStyle(size, error)), [`&${focusSelector}`]: Object.assign(Object.assign({}, getButtonStateStyle(size, error)), { border: `2px solid ${theme.colors.apolloColorStrokeFocus} !important`, paddingLeft: `${getValueFromPixelString(apolloSpaceInset1X) - 2}px !important`, paddingRight: `${getValueFromPixelString(error ? apolloSpaceInset6X : apolloSpaceInset3X) - 2}px !important`, paddingTop: `${getValueFromPixelString(size === "normal" ? apolloSpaceInsetHalfX : apolloSpaceInset1X) - 2}px !important`, [`${StyledIcon}`]: {
            marginRight: "-1px !important",
        }, [`${StyledErrorIconContainer}`]: {
            marginRight: `${getValueFromPixelString(apolloSpaceInset3X) - 1}px !important`,
        } }), [`&&:active, &&${focusSelector}:active`]: Object.assign({ background: theme.colors.apolloColorBackgroundFormElementOpen, borderColor: "transparent", boxShadow: theme.colors.apolloBoxShadowFormElementOpen }, getButtonStateStyle(size, error, !isDisabled)) }), (error && {
    "&&": {
        borderColor: theme.colors.apolloColorStrokeError,
    },
})), (isDisabled && {
    [`&&, &&${focusSelector}, &&:hover, &&:active, &&${focusSelector}:active`]: {
        background: theme.colors.apolloColorBackgroundFormElementDisabled,
        borderColor: theme.colors.apolloColorStrokeFormElementDisabled,
        boxShadow: theme.colors.apolloBoxShadowFormElementDisabled,
        color: theme.colors.apolloColorTextDisabled,
        cursor: "auto",
    },
})), (isOpen && {
    [`&&, &&${focusSelector}, &&:active`]: Object.assign({ background: theme.colors.apolloColorBackgroundFormElementOpen, borderColor: "transparent", boxShadow: theme.colors.apolloBoxShadowFormElementOpen }, getButtonStateStyle(size, error, true)),
})), { "& > span": {
        position: "relative",
    } }));
export const StyledSelectButton = styled(forwardRef(function StyledSelectButton({ id, className, tooltip, isDisabled, isOpen, dropdownId, children, onClick, "data-insights": insightsAttr, onKeyDown, "aria-labelledby": ariaLabelledBy, "aria-label": ariaLabel, "aria-describedby": ariaDescribedBy, unstyled, }, ref) {
    return (React.createElement("button", Object.assign({ id: id, "data-insights": insightsAttr, type: "button", className: className, title: tooltip, onClick: !isDisabled ? onClick : undefined, "aria-expanded": isOpen, "aria-disabled": isDisabled, "aria-controls": dropdownId, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy || (!ariaLabel ? `${id}-label` : undefined), "aria-describedby": ariaDescribedBy, tabIndex: isDisabled ? -1 : 0, ref: ref, onKeyDown: onKeyDown }, (!unstyled && { "data-focus-border": true })),
        React.createElement("span", Object.assign({ id: `${id}-label` }, (!ariaLabel && !ariaLabelledBy && { "aria-hidden": true })), children),
        !unstyled && React.createElement(StyledIcon, { isDisabled: isDisabled })));
})).withConfig({ displayName: "StyledSelectButton_APC-7281-productShell", componentId: "sc-uywv51" })(({ theme, isDisabled, isOpen, error, size, unstyled }) => {
    if (unstyled) {
        return {
            textAlign: "left",
            background: "transparent",
            margin: 0,
            padding: 0,
            cursor: "pointer",
            // the global style has a focus ring with dynamic specificity (depending on its nesting level)
            // so it's preferable to have !important here to be sure to override it
            border: "none !important",
            outline: "none !important",
        };
    }
    return getButtonStyles(theme, isDisabled, isOpen, !!error, size);
});
export const StyledPlaceHolder = styled.span.withConfig({ displayName: "StyledPlaceHolder_APC-7281-productShell", componentId: "sc-qg9fg0" })(({ theme }) => (Object.assign({ color: theme.colors.apolloColorTextTertiary }, apolloTheme.textStyles.bodyEmphasis)));
export const StyledClearSelectionButton = styled(Button).withConfig({ displayName: "StyledClearSelectionButton_APC-7281-productShell", componentId: "sc-niagd8" })({
    width: "100%",
});
//# sourceMappingURL=Select.style.js.map